import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { errorHandler } from "../utils/errorHandler";
import { useAuth } from "../contexts/AuthContext";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

const Container = styled.div`
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  margin: 0;
  padding: 0;
  color: #333;
  background: #000000;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainCard = styled.div`
  max-width: 500px;
  width: 90%;
  background: white;
  border-radius: 25px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: ${fadeIn} 1s ease-out;
  margin: 40px 20px;

  @media (max-width: 768px) {
    width: 95%;
    margin: 20px 10px;
    border-radius: 15px;
  }
`;

const Header = styled.div`
  background: #000000;
  color: white;
  padding: 40px 30px;
  text-align: center;
  border-bottom: 3px solid #e30613;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Logo = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #e30613;
`;

const Subtitle = styled.p`
  opacity: 0.9;
  font-size: 1.1rem;
  margin: 0;
`;

const Form = styled.form`
  padding: 40px 30px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #f8f9fa;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #e30613;
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(227, 6, 19, 0.1);
  }

  &.error {
    border-color: #ff4757;
    animation: ${shake} 0.5s ease-in-out;
  }

  @media (max-width: 768px) {
    padding: 18px;
    font-size: 16px; /* Prevents zoom on iOS */
    border-radius: 10px;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  background: #e30613;
  color: white;
  border: none;
  padding: 18px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(227, 6, 19, 0.3);
    animation: ${pulse} 0.6s ease-in-out;
    background: #c00510;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 20px;
    font-size: 1.2rem;
    border-radius: 10px;
    margin-top: 20px;
  }
`;

const ErrorMessage = styled.div`
  background: #ff4757;
  color: white;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
  animation: ${shake} 0.5s ease-in-out;
`;

const SuccessMessage = styled.div`
  background: #2ed573;
  color: white;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 500;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ForgotPassword = styled(Link)`
  display: block;
  text-align: center;
  color: #e30613;
  text-decoration: none;
  margin-top: 20px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: #c00510;
    transform: translateY(-1px);
  }
`;

const SignupLink = styled.div`
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
  color: #666;
`;

const SignupLinkButton = styled(Link)`
  color: #e30613;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    color: #c00510;
    transform: translateY(-1px);
  }
`;


const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithCredentials, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rememberMe, setRememberMe] = useState(true);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError("");
  };


  const validateForm = () => {
    if (!formData.email) {
      setError("Por favor, insira seu email");
      return false;
    }
    if (!formData.password) {
      setError("Por favor, insira sua senha");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError("Por favor, insira um email válido");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      // Use the new API-based login method that handles CSRF tokens
      const loginResult = await loginWithCredentials(formData, rememberMe);
      
      if (loginResult.success) {
        setSuccess("Login realizado com sucesso!");
        
        // Get redirect path from location state or default to dashboard
        const from = location.state?.from?.pathname || '/dashboard';
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate(from, { replace: true });
        }, 1500);
      } else {
        throw new Error(loginResult.error || "Credenciais inválidas");
      }

    } catch (err) {
      const handledError = errorHandler.handleError(err, { action: "login" });
      setError(handledError.message || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <MainCard>
        <Header>
          <Logo>
            <span>⚡</span>
            Beatt Pulse
          </Logo>
          <Subtitle>Faça login em sua conta</Subtitle>
        </Header>

        <Form onSubmit={handleSubmit}>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="seu@email.com"
              className={error && !formData.email ? "error" : ""}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Senha</Label>
            <Input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Sua senha"
              className={error && !formData.password ? "error" : ""}
            />
          </FormGroup>

          <FormGroup>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ margin: 0 }}
              />
              <Label htmlFor="rememberMe" style={{ margin: 0, fontSize: '0.9rem', cursor: 'pointer' }}>
                Lembrar de mim
              </Label>
            </div>
          </FormGroup>

          <LoginButton type="submit" disabled={loading}>
            {loading && <LoadingSpinner />}
            {loading ? "Entrando..." : "Entrar"}
          </LoginButton>

          <ForgotPassword to="/forgot-password">
            Esqueceu sua senha?
          </ForgotPassword>

          {/* Cadastro público temporariamente desabilitado */}
          {/* 
          <SignupLink>
            Não tem uma conta?{" "}
            <SignupLinkButton to="/cadastro">
              Cadastre-se gratuitamente
            </SignupLinkButton>
          </SignupLink>
          */}
        </Form>
      </MainCard>
    </Container>
  );
};

export default Login; 